import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalShopSettings from "@faire/web-api--source/routes/brand-portal/my-shop/settings";
import brandPortalOrders from "@faire/web-api--source/routes/brand-portal/orders";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ScheduledOrdersIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You may choose to let retailers place scheduled orders with your shop. If you enable <BoldFormatting>Scheduled orders</BoldFormatting> that will allow retailers to choose the date they’d like to receive the order, up to six months in advance. When you accept a scheduled order, ensure you’ll have the products available to fulfill the order on the scheduled date."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Introduction to scheduled orders",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Here’s how to change your setting:"
          description={{
            text: "Header for changing settings",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to the <BoldFormatting>Settings</BoldFormatting> page."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "First step to change settings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <LinkFormatting>Shop settings</LinkFormatting>."
              values={{
                LinkFormatting: ShopSettingsLinkFormatting,
              }}
              description={{
                text: "Second step to change settings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Scroll down to <BoldFormatting>Fulfillment options</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to change settings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Toggle <BoldFormatting>Scheduled orders</BoldFormatting> to <BoldFormatting>On</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to change settings",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Save</BoldFormatting> to confirm your changes."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to change settings",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can find all scheduled orders in your <LinkFormatting>Orders</LinkFormatting> page. These orders are listed alongside your other orders in the same section. To help with fulfillment preparation, you can sort your orders by ship date to see orders scheduled farther in the future."
          values={{
            LinkFormatting: OrdersLinkFormatting,
          }}
          description={{
            text: "Information about finding scheduled orders",
          }}
        />
      </Typography>
    </Column>
  );
};

const ShopSettingsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalShopSettings()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const OrdersLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
